import { useState } from "react";
import { createAuthenticatedAxiosInstance, postSize } from "api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import { faExclamationTriangle, faCheckCircle, faCircleInfo, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { ICancelContinueModal, IConfirmModal } from "interfaces/IModal";
import { AdminRouteContainer } from "components/general/AdminRouteContainer";
import { Button, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { FeedbackBorderContainer } from "components/general/FeedbackBorderContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ModalHandler } from "components/general/ModalHandler";
import logo from "../../assets/logo.png";
import { isSuccessStatusCode } from "helpers/httpResponse";

/**
 * The add size component
 */
export const AddSizes = () => {
    const { token } = useAuthStore();
    const axiosInstance = createAuthenticatedAxiosInstance(token);
    const [nameInput, setNameInput] = useState<string>("");
    const [labelInput, setLabelInput] = useState<string>("");
    const [nameValidation, setNameValidation] = useState<boolean | undefined>(undefined);
    const [labelValidation, setLabelValidation] = useState<boolean | undefined>(undefined);
    const [nameMessage, setNameMessage] = useState<string>("");
    const [labelMessage, setLabelMessage] = useState<string>("");
    const initialModal = {
        show: false,
        onHide: () => { },
        onConfirm: () => { },
        title: "Atención",
        message: "¿Estás seguro de agregar una nueva talla?",
        icon: faExclamationTriangle,
        iconColor: "text-danger"
    };
    const [modal, setModal] = useState<IConfirmModal | ICancelContinueModal>(initialModal);

    /**
     * Method that calls api endpoint to create a size
     */
    const addSize = async () => {
        const trimmedName = nameInput.trim();
        const trimmedLabel = labelInput.trim();
        const isValid = trimmedName.length <= 50 && trimmedLabel.length <= 50;
        const newSize = {
            name: trimmedName,
            description: trimmedLabel
        }
        const successModal: IConfirmModal = {
            title: "Confirmación",
            message: "La talla se ha guardado con éxito.",
            icon: faCheckCircle,
            iconColor: "text-success",
            onHide: () => resetValues(),
            show: true
        }
        const errorModal: IConfirmModal = {
            title: "Confirmación",
            message: "Error al conectar con el servidor. Inténtalo más tarde.",
            icon: faTimesCircle,
            iconColor: "text-danger",
            onHide: onHideModal,
            show: true
        }
        if (!isValid)
            return;
        const { statusCode, result } = await postSize(axiosInstance, newSize);
        if (!isSuccessStatusCode(statusCode)) {
            const nameAlreadyTaken = result?.errors?.name;
            const descriptionAlreadyTaken = result?.errors?.description;
            if (nameAlreadyTaken || descriptionAlreadyTaken) {
                setModal(
                    {
                        ...errorModal,
                        message: `La talla no se pudo agregar: ${nameAlreadyTaken ? "\nEl nombre ya existe" : ""} ${descriptionAlreadyTaken ? "\nLa descripción ya existe" : ""}`
                    }
                );
                return;
            }
            setModal(errorModal);
            return;
        }
        setNameValidation(true);
        setLabelValidation(true);
        setModal(successModal);
    }

    /**
     * Resets the values of the inputs shown on the add size component
     */
    const resetValues = () => {
        setNameValidation(undefined);
        setLabelValidation(undefined);
        setLabelInput("");
        setNameInput("");
        setModal(initialModal);
    }

    /**
     * Hides the modal and set the default initial modal
     */
    const onHideModal = () => setModal(initialModal);

    /**
     * Sets the modal to the next save confirmation modal
     */
    const onSave = () => {
        const trimmedName = nameInput.trim();
        const trimmedLabel = labelInput.trim();
        const isAnyFieldEmpty = trimmedName.length <= 0 || trimmedLabel.length <= 0;
        const isAnyFieldTooLong = trimmedName.length >= 50 || trimmedLabel.length >= 50;
        if (isAnyFieldEmpty) {
            setNameValidation(trimmedName.length == 0 ? false : undefined);
            setLabelValidation(trimmedLabel.length == 0 ? false : undefined);
            setNameMessage("Este campo es obligatorio.");
            setLabelMessage("Este campo es obligatorio.");
            return;
        }
        if (isAnyFieldTooLong) {
            setNameValidation(trimmedName.length >= 50 ? false : undefined);
            setLabelValidation(trimmedLabel.length >= 50 ? false : undefined);
            setNameMessage("El nombre es demasiado largo.");
            setLabelMessage("El distintivo es demasiado largo.");
            return;
        }
        setModal({
            ...modal,
            show: true,
            onHide: onHideModal,
            onConfirm: addSize
        });
    }

    /**
     * Sets the modal to the next reset confirmation modal
     */
    const onReset = () => {
        setModal({
            ...modal,
            message: "Estas seguro de restablecer la información?\nLos datos no guardados no surtirán efecto",
            show: true,
            onHide: onHideModal,
            onConfirm: resetValues
        });
    }

    return (
        <AdminRouteContainer customMaxWidth={1000} customClass="align-items-stretch">
            <>
                <Row className="justify-content-center text-center">
                    <img
                        src={logo}
                        alt="Logo de la Boutique"
                        className="mb-4"
                        style={{ width: "215px" }}
                    />
                    <h2 className="fw-bold">Crear talla</h2>
                </Row>
                <Row className="align-items-center mb-4 mt-5">
                    <Form>
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold">
                                Nombre de la talla:
                            </Form.Label>
                            <FeedbackBorderContainer responseState={nameValidation} message={nameMessage}>
                                <Form.Control
                                    type="text"
                                    onChange={(e) => {
                                        setNameValidation(undefined);
                                        setNameInput(e.target.value);
                                    }}
                                    value={nameInput}
                                    readOnly={false}
                                    placeholder="Nombre de la talla "
                                />
                            </FeedbackBorderContainer>
                        </Form.Group>
                        <Form.Group className="mb-4 text-center justify-items-center">
                            <Form.Label className="fw-bold">
                                Distintivo
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip className="custom-tooltip">
                                            EL valor distintivo es el que verá el cliente al momento de hacer su compra.
                                            <br />
                                            Ej. S, M, L o CH, M, G.
                                        </Tooltip>
                                    }
                                >
                                    <FontAwesomeIcon icon={faCircleInfo} className="ms-2" />
                                </OverlayTrigger>
                            </Form.Label>
                            <FeedbackBorderContainer responseState={labelValidation} message={labelMessage}>
                                <Form.Control
                                    style={{ width: "20%", justifySelf: "center" }}
                                    type="text"
                                    onChange={(e) => {
                                        setLabelValidation(undefined);
                                        setLabelInput(e.target.value)
                                    }}
                                    value={labelInput}
                                    readOnly={false}
                                    placeholder="Distintivo "
                                />
                            </FeedbackBorderContainer>
                        </Form.Group>
                    </Form>
                </Row>
                <Row className="d-flex justify-content-between" >
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={onReset}
                        className=" fw-bold align-item-center text-center ms-2"
                        style={{ width: "14%" }}
                    >
                        Restablecer
                    </Button>
                    <Button
                        variant="primary"
                        type="button"
                        onClick={onSave}
                        className=" fw-bold align-item-center text-center me-2"
                        style={{ width: "14%" }}
                    >
                        Guardar
                    </Button>
                </Row>
                <ModalHandler modal={modal} />
            </>
        </AdminRouteContainer>
    )
}
